import { action, observable, runInAction } from 'mobx';
import { BasePreviewViewStore } from 'core/stores';
import { applicationContext } from 'core/utils';
import { TrackingCaseService, NoteService, TrackingCaseDocumentStreamService, TrackingCaseDocumentService } from "application/tracking-case/services";

import { ModalParams } from 'core/models';
import _ from 'lodash';

@applicationContext
class TrackingCasePreviewViewStore extends BasePreviewViewStore {
    @observable accessor item; // this is important when adding / uploading documents
    @observable accessor notes;

    constructor(rootStore) {
        const id = rootStore.routerStore.routerState.params.id;
        const service = new TrackingCaseService(rootStore.application.baasic.apiClient);
        const trackingCaseDocumentService = new TrackingCaseDocumentService(rootStore.application.baasic.apiClient);
        const noteService = new NoteService(rootStore.application.baasic.apiClient);

        super(rootStore, {
            name: 'tracking-case-preview',
            autoInit: true,
            id: id,
            routes: {
                edit: () => {
                    this.rootStore.routerStore.goTo(
                        'master.app.main.tracking-case.edit',
                        { id: this.id });
                },
                documentList: () => {
                    this.rootStore.routerStore.goTo(
                        'master.app.main.tracking-case.documents',
                        { id: this.id });
                },
                fieldValues: id => {
					this.rootStore.routerStore.goTo("master.app.main.tracking-case.documents.field-values", { id: id });
				}
            },
            actions: () => {
                return {
                    get: async (id) => {
                        const response = await service.get(id, { embed: 'patient,facility,itemCategory,caseStatus,trackingCaseProducts,trackingCaseRequirements,trackingCaseDocuments' });
                        this.getNotes();
                        return response.data;
                    },
                    copy: async resource => {
                        const response = await service.copy(resource);
                        return response.data;
                    },
                    deleteNote: noteService.delete,
                    submitDocument: async resource => {
                    const response = await trackingCaseDocumentService.submit(resource);
                    return response.data;
                   },
                   deleteDocument: trackingCaseDocumentService.delete,
                   batchDeleteDocuments: trackingCaseDocumentService.batchDelete
                }
            }
        });

        this.id = id;
        this.noteService = noteService;

        this.uploadDocumentModal = new ModalParams({});
        this.editDocumentModal = new ModalParams({});
        this.editNoteModal = new ModalParams({});

        this.streamService = new TrackingCaseDocumentStreamService(rootStore.application.baasic.apiClient);
        this.apiDownloadEndpoint =`${this.streamService.apiClient.appOptions.apiUrl}/${this.streamService.routeService.base}`;
    }

    @action.bound
    async fetchItem() {
        var item = await this.actions.get(this.id);
     
        runInAction(() =>{
            this.item = item;
        });
    }

    @action.bound
    async getNotes() {
        const response = await this.noteService.find({rpp:10, sort:"dateCreated|desc", trackingCaseId: this.id, embed: 'author'}); 
        runInAction(() =>{
            this.notes = response.data.item;
        });
    }

    @action.bound
    openUploadDocumentPopup() {
        this.uploadDocumentModal.open({
            trackingCaseId: this.id,
            fetchTable: () => {
                this.fetchItem();
            }
        });
    }

    @action.bound
    openEditDocumentPopup() {
        this.editDocumentModal.open({
            trackingCaseId: this.id,
            fetchTable: () => {
                this.fetchItem();
            }
        });
    }

    @action.bound
    openEditNotePopup(id) {
        this.editNoteModal.open({
            id: id,
            trackingCaseId: this.id,
            fetchTable: () => {
                this.getNotes();
            }
        });
    }
    
    @action.bound
    openDocumentsPage() {
        this.routes.documentList()
    }
 
    @action.bound
    openTrackingCaseEdit() {
        this.routes.edit()
    }

    @action.bound
    async duplicate() {
        const { modalStore, notificationStore } = this.rootStore;

		modalStore.showConfirm(
            'LIST_LAYOUT.CONFIRM_COPY',
            async () => {
				try {

					await this.actions.copy({ id: this.id });

					notificationStore.success("TRACKING_CASE.COPY.SUCCESS");
				} catch(err){
					notificationStore.error("TRACKING_CASE.COPY.ERROR", err);
				}

                this.rootStore.routerStore.goTo("master.app.main.tracking-case.list");
			},
			() => {
                modalStore.confirmParams.close();
            }
		);
	}

    @action.bound
    deleteNote(noteId) {
        const { modalStore, notificationStore } = this.rootStore;

        modalStore.showConfirm("LIST_LAYOUT.CONFIRM_DELETE", async () => {
            try {
                await this.actions.deleteNote({ id: noteId });
                this.getNotes();
                notificationStore.success("LIST_LAYOUT.SUCCESS_DELETE");
            } catch (err) {
                notificationStore.error(
                    err.data && typeof err.data !== "object"
                        ? err.data
                        : "LIST_LAYOUT.ERROR_DELETE",
                    err
                );
            }
        });
    }

    @action.bound
    async onDocumentView(id) {
        let url = (`${this.rootStore.routerStore.findRoute('master.app.main.tracking-case.documents.field-values-edit').pattern}`).replace(":trackingCaseDocumentId", id);
        window.open(url);
    }
    
    @action.bound
    async onFieldValues(id) {
        this.routes.fieldValues(id);
    }  
    
    @action.bound
    onSubmit(resource) {
        if (!this.actions.submitDocument) return;
        const { modalStore, notificationStore } = this.rootStore;

        modalStore.showConfirm(
            'TRACKING_CASE_DOCUMENT.SUBMIT.CONFIRM_SUBMIT',
            async () => {
                try {
                    this.actions.submitDocument({id: resource.id});
                    notificationStore.success('TRACKING_CASE_DOCUMENT.SUBMIT.SUCCESS_SUBMIT');
                    //await this.queryUtility.fetch();
                    this.fetchItem();
                } catch (err) {
                    notificationStore.error('TRACKING_CASE_DOCUMENT.SUBMIT.ERROR_SUBMIT', err);
                }
            }
        );
    }

    @action.bound
    async onPreview(clientAttachment) {
        window.open(`${this.apiDownloadEndpoint}/${clientAttachment.id}?contentDisposition=inline`);
    }

    @action.bound
    async onDownload(clientAttachment) {
        window.open(`${this.apiDownloadEndpoint}/${clientAttachment.id}?contentDisposition=attachment`); // This shortly open file in new window/tab. Pros about this is if file is large, instantly start downloading, instead of next solution where file is started downloading when backend returned 100%
    }

    @action.bound
    onDelete(resource) {
        if (!this.actions.deleteDocument) return;

        const { modalStore, notificationStore } = this.rootStore;
        modalStore.showConfirm(
            'TRACKING_CASE_DOCUMENT.DELETE.CONFIRM_DELETE',
            async () => {
                try {
                    if (_.isArray(resource)) {
                        await this.actions.batchDeleteDocuments(resource);
                    }
                    else {
                        await this.actions.deleteDocument({id: resource.id});
                    }
                    notificationStore.success('TRACKING_CASE_DOCUMENT.DELETE.SUCCESS_DELETE');
                    //await this.queryUtility.fetch();
                    this.fetchItem();
                } catch (err) {
                    notificationStore.error(err.data && typeof err.data !== "object" ? err.data: 'TRACKING_CASE_DOCUMENT.DELETE.ERROR_DELETE', err);
                }
            }
        );
    }
}

export default TrackingCasePreviewViewStore;
